import React from 'react';
import {
    Box,
    TextField,
    Typography,
    Grid,
    Paper,
    RadioGroup,
    FormControlLabel,
    Radio,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

const PersonForm = ({ title, baseName, isClient }) => {
    const { control, formState: { errors } } = useFormContext();

    // Get nested errors for this person
    const personErrors = errors[baseName] || {};

    return (
        <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                {title} {isClient && <span style={{ color: '#1976d2', fontWeight: 600 }}>(client)</span>}
            </Typography>

            <Grid container spacing={2}>
                {/*
                  Phase 3b: required-field relaxation. Per-field rules keep
                  data-correctness checks (length minimums, character pattern,
                  date relationships) but only fire when a value is provided.
                  Empty fields are saveable — agent or user fills in over time.
                */}
                <Grid item xs={12} md={4}>
                    <Controller
                        name={`${baseName}.firstName`}
                        control={control}
                        rules={{
                            minLength: {
                                value: 2,
                                message: 'First name must be at least 2 characters'
                            },
                            pattern: {
                                value: /^[A-Za-z\s'-]+$/,
                                message: 'First name can only contain letters, spaces, hyphens, and apostrophes'
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="First Name"
                                error={!!personErrors.firstName}
                                helperText={personErrors.firstName?.message}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Controller
                        name={`${baseName}.middleName`}
                        control={control}
                        rules={{
                            pattern: {
                                value: /^[A-Za-z\s'-]*$/,
                                message: 'Middle name can only contain letters, spaces, hyphens, and apostrophes'
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Middle Name"
                                error={!!personErrors.middleName}
                                helperText={personErrors.middleName?.message}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Controller
                        name={`${baseName}.lastName`}
                        control={control}
                        rules={{
                            minLength: {
                                value: 2,
                                message: 'Last name must be at least 2 characters'
                            },
                            pattern: {
                                value: /^[A-Za-z\s'-]+$/,
                                message: 'Last name can only contain letters, spaces, hyphens, and apostrophes'
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Last Name"
                                error={!!personErrors.lastName}
                                helperText={personErrors.lastName?.message}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Controller
                        name={`${baseName}.birthDate`}
                        control={control}
                        rules={{
                            validate: (value) => {
                                if (!value) return true; // Empty is allowed — no longer required.
                                const birthDate = new Date(value);
                                const today = new Date();
                                const eighteenYearsAgo = new Date();
                                eighteenYearsAgo.setFullYear(today.getFullYear() - 18);

                                if (birthDate > today) {
                                    return 'Birth date cannot be in the future';
                                }

                                if (birthDate > eighteenYearsAgo) {
                                    return 'Person must be at least 18 years old';
                                }

                                if (birthDate < new Date('1900-01-01')) {
                                    return 'Birth date must be after 1900';
                                }

                                return true;
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Birth Date"
                                type="date"
                                error={!!personErrors.birthDate}
                                helperText={personErrors.birthDate?.message}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        )}
                    />
                </Grid>

                {/* No more checkbox — client is set via the radio group above */}
            </Grid>
        </Box>
    );
};

const PetitionerRespondentForm = () => {
    const { control, setValue } = useFormContext();

    // Watched so the (client) badge on the active party header updates.
    const clientSelection = useWatch({ control, name: 'clientSelection' });

    return (
        <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Parties
            </Typography>

            {/* Radio group for selecting which party is the client.
                Phase 3b: no longer required — interview agent or admin can
                designate later when the information is known. */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Client</Typography>
                <Controller
                    name="clientSelection"
                    control={control}
                    render={({ field }) => (
                        <RadioGroup
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => {
                                setValue('petitioner.client', e.target.value === 'petitioner');
                                setValue('respondent.client', e.target.value === 'respondent');
                                field.onChange(e.target.value);
                            }}
                        >
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        value="petitioner"
                                        control={<Radio />}
                                        label="Petitioner is the client"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        value="respondent"
                                        control={<Radio />}
                                        label="Respondent is the client"
                                    />
                                </Grid>
                            </Grid>
                        </RadioGroup>
                    )}
                />
            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <PersonForm
                        title="Petitioner"
                        baseName="petitioner"
                        isClient={clientSelection === 'petitioner'}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <PersonForm
                        title="Respondent"
                        baseName="respondent"
                        isClient={clientSelection === 'respondent'}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};

export default PetitionerRespondentForm;
